import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  FirestoreDataConverter,
  orderBy,
  query,
  setDoc,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { useCollectionData } from 'react-firebase-hooks/firestore';
import { firestore } from '@/lib/firebase';

/** Alerting opens this long before kickoff. */
export const ALERT_LEAD_MINUTES = 5 * 60; // 5 h
/** …and closes this long after it. */
export const ALERT_TRAIL_MINUTES = 3.5 * 60; // 3 h 30 m

/** Game-day window used when the kickoff time is still TBA (local/ET hours). */
export const TBD_WINDOW_START_HOUR = 7;
export const TBD_WINDOW_END_HOUR = 24;

export const EVENT_TEAMS = ['DOLPHINS', 'HURRICANES', 'OTHER'] as const;
export type EventTeam = (typeof EVENT_TEAMS)[number];

export const TEAM_LABELS: Record<EventTeam, string> = {
  DOLPHINS: 'Miami Dolphins',
  HURRICANES: 'Miami Hurricanes',
  OTHER: 'Other event',
};

export interface AlertEvent {
  id?: string;
  team: EventTeam;
  opponent: string;
  /** Kickoff instant. For TBA games this is a placeholder used only for the date. */
  kickoff: Date;
  /** Kickoff time not announced yet — the whole game day is covered instead. */
  timeTbd: boolean;
  /** Free-form tag: "Preseason", "Homecoming", "flex" … */
  note?: string;
  /** Denormalised window bounds so the agent needs no timezone math. */
  windowStart: Date;
  windowEnd: Date;
}

/** Window an event covers: kickoff −5 h → +3 h 30, or the whole day when TBA. */
export function eventWindow(kickoff: Date, timeTbd: boolean) {
  if (timeTbd) {
    const start = new Date(kickoff);
    start.setHours(TBD_WINDOW_START_HOUR, 0, 0, 0);
    const end = new Date(kickoff);
    end.setHours(0, 0, 0, 0);
    end.setHours(TBD_WINDOW_END_HOUR, 0, 0, 0);
    return { start, end };
  }
  return {
    start: new Date(kickoff.getTime() - ALERT_LEAD_MINUTES * 60_000),
    end: new Date(kickoff.getTime() + ALERT_TRAIL_MINUTES * 60_000),
  };
}

const converter: FirestoreDataConverter<AlertEvent> = {
  toFirestore: () => {
    throw new Error('use the mutation helpers');
  },
  fromFirestore: (snapshot) => {
    const d = snapshot.data();
    const kickoff: Date = d.kickoff?.toDate?.() ?? new Date(0);
    const timeTbd = !!d.timeTbd;
    const fallback = eventWindow(kickoff, timeTbd);
    return {
      id: snapshot.id,
      team: (d.team ?? 'OTHER') as EventTeam,
      opponent: d.opponent ?? '',
      kickoff,
      timeTbd,
      note: d.note ?? '',
      windowStart: d.windowStart?.toDate?.() ?? fallback.start,
      windowEnd: d.windowEnd?.toDate?.() ?? fallback.end,
    };
  },
};

export function useAlertEvents() {
  return useCollectionData(
    query(
      collection(firestore, 'alertEvents').withConverter(converter),
      orderBy('kickoff', 'asc')
    ),
    { snapshotListenOptions: { includeMetadataChanges: false } }
  );
}

type EventInput = {
  team: EventTeam;
  opponent: string;
  kickoff: Date;
  timeTbd: boolean;
  note?: string;
};

function payload(input: EventInput) {
  const { start, end } = eventWindow(input.kickoff, input.timeTbd);
  return {
    team: input.team,
    opponent: input.opponent.trim(),
    kickoff: Timestamp.fromDate(input.kickoff),
    timeTbd: input.timeTbd,
    note: input.note?.trim() ?? '',
    windowStart: Timestamp.fromDate(start),
    windowEnd: Timestamp.fromDate(end),
  };
}

export function addAlertEvent(input: EventInput) {
  return addDoc(collection(firestore, 'alertEvents'), payload(input));
}

/** Upsert under a caller-chosen id — used by the season import so it is idempotent. */
export function upsertAlertEvent(id: string, input: EventInput) {
  return setDoc(doc(firestore, 'alertEvents', id), payload(input));
}

export function updateAlertEvent(id: string, input: EventInput) {
  return updateDoc(doc(firestore, 'alertEvents', id), payload(input));
}

export function removeAlertEvent(id: string) {
  return deleteDoc(doc(firestore, 'alertEvents', id));
}

/** The event whose window contains `now`, if any. */
export function activeEvent(events: AlertEvent[] | undefined, now: number) {
  return (events ?? []).find(
    (e) => e.windowStart.getTime() <= now && e.windowEnd.getTime() > now
  );
}

/** The next event whose window has not opened yet. */
export function nextEvent(events: AlertEvent[] | undefined, now: number) {
  return (events ?? [])
    .filter((e) => e.windowStart.getTime() > now)
    .sort((a, b) => a.windowStart.getTime() - b.windowStart.getTime())[0];
}
